// Transactions slice — one sub-slice per family, all from the same factory.
//
// The previous module had three hand-written slices with the same bugs in each.
// This builds all three from the family table, so a fix lands everywhere.

import * as types from '../../lib/actionTypes.es6';
import { FAMILIES } from './families.es6';
import {
  createListReducer,
  initialListState
} from '../../components/CommissionsShared/listReducerFactory.es6';

function initialStatsState() {
  return { data: null, loading: false, error: null };
}

function createStatsReducer(types) {
  return function statsReducer(state, action) {
    if (state === undefined) state = initialStatsState();
    switch (action.type) {
      case types.request:
        return { data: state.data, loading: true, error: null };
      case types.success:
        // Stats bodies differ per family and are stored verbatim; the cards
        // read the fields their own family declares.
        return { data: action.payload || null, loading: false, error: null };
      case types.failure:
        return {
          data: null,
          loading: false,
          error: action.payload || { message: 'Could not load statistics' }
        };
      default:
        return state;
    }
  };
}

function createFamilyReducer(family) {
  var listReducer = createListReducer(
    {
      request: family.types.list.request,
      success: family.types.list.success,
      failure: family.types.list.failure,
      detailSuccess: family.types.detail.success
    },
    { keepRowsWhileLoading: true }
  );

  // The per-agent list is a separate view of the same records, so it gets its
  // own list state rather than fighting the main one for `guids`.
  var byAgentReducer = createListReducer(
    {
      request: family.types.byAgent.request,
      success: family.types.byAgent.success,
      failure: family.types.byAgent.failure
    },
    { keepRowsWhileLoading: true }
  );

  var statsReducer = createStatsReducer(family.types.stats);

  return function familyReducer(state, action) {
    if (state === undefined) {
      state = {
        list: initialListState(),
        byAgent: initialListState(),
        stats: initialStatsState(),
        listParams: {},
        detailLoading: false,
        detailError: null
      };
    }

    var next = state;

    var list = listReducer(state.list, action);
    var byAgent = byAgentReducer(state.byAgent, action);
    var stats = statsReducer(state.stats, action);

    if (list !== state.list || byAgent !== state.byAgent ||
        stats !== state.stats) {
      next = Object.assign({}, state, {
        list: list,
        byAgent: byAgent,
        stats: stats
      });
    }

    switch (action.type) {
      case family.types.list.request:
        return Object.assign({}, next, { listParams: action.payload || {} });
      case family.types.detail.request:
        return Object.assign({}, next, {
          detailLoading: true,
          detailError: null
        });
      case family.types.detail.success:
        return Object.assign({}, next, {
          detailLoading: false,
          detailError: null
        });
      case family.types.detail.failure:
        return Object.assign({}, next, {
          detailLoading: false,
          detailError: action.payload || {
            message: 'Could not load this transaction'
          }
        });
      default:
        return next;
    }
  };
}

var calculationLogListReducer = createListReducer(
  {
    request: types.GET_COMMISSION_CALCULATION_LOG_REQUEST,
    success: types.GET_COMMISSION_CALCULATION_LOG_SUCCESS,
    failure: types.GET_COMMISSION_CALCULATION_LOG_FAILURE
  },
  { keepRowsWhileLoading: true }
);

function calculationLogReducer(state, action) {
  if (state === undefined) {
    state = { list: initialListState(), listParams: {} };
  }
  var list = calculationLogListReducer(state.list, action);
  var listParams =
    action.type === types.GET_COMMISSION_CALCULATION_LOG_REQUEST
      ? action.payload || {}
      : state.listParams;
  if (list === state.list && listParams === state.listParams) return state;
  return { list: list, listParams: listParams };
}

var subReducers = {};
FAMILIES.forEach(function(family) {
  subReducers[family.key] = createFamilyReducer(family);
});
subReducers.calculationLog = calculationLogReducer;

export function initialState() {
  var state = {};
  Object.keys(subReducers).forEach(function(key) {
    state[key] = subReducers[key](undefined, { type: '@@INIT' });
  });
  return state;
}

export default function commissionTransactionsReducer(state, action) {
  if (state === undefined) state = initialState();
  if (!action || !action.type) return state;

  var next = state;
  var changed = false;

  Object.keys(subReducers).forEach(function(key) {
    var before = state[key];
    var after = subReducers[key](before, action);
    if (after !== before) {
      if (!changed) {
        next = Object.assign({}, state);
        changed = true;
      }
      next[key] = after;
    }
  });

  return next;
}
